// material-ui
import { Typography,Grid, Button, Stack, Input, Alert, Table, TableBody, TableCell, TableRow, TableHead, Select, MenuItem, FormHelperText, Skeleton, TextField } from '@mui/material';

// project import
import MainCard from 'components/MainCard';
import React from 'react';
import { getRequest, postRequest } from 'utils/apiRequest';
import * as Yup from 'yup';
import { Formik } from 'formik';
import { LoadingButton } from '@mui/lab';
import { Notice } from 'components/Notice';
import CircleLoader from 'components/CircleLoader';

// ==============================|| SAMPLE PAGE ||============================== //

const CarrierSchema = Yup.object().shape({
    carrier: Yup.string().max(15).required('Carrier is required'),
    transitDelayHours: Yup.number().min(0).max(730).required('Transit Delay is required and must be greater than 0'),
})
const CarrierTransitSchema = Yup.object().shape({
    transitDelayHours: Yup.number().min(0).max(730).required('Transit Delay is required and must be greater than 0'),
})

const AddNewCarrier = ({ existingCarrierIds, onSuccess = () => { } }) => {
    const [carriers, setCarriers] = React.useState([]);
    const [loading, setLoading] = React.useState(true);
    const [success, setSuccess] = React.useState(false);
    const [mounted, setMounted] = React.useState(false);
    React.useEffect(() => {
        setMounted(true);
        getRequest('/carriers').then((d) => {
            if(d){
                setCarriers(d.filter((c, i)=>existingCarrierIds.indexOf(c.id) === -1));
            }
        }).finally(()=>{
            setLoading(false)
        })
        return ( ) => {
            setMounted(false);
        }
    }, []);

    return (loading ? <CircleLoader /> : (<Formik
        initialValues={{
            carrier: carriers[0].id,
            transitDelayHours: 0,
            submit: null
        }}
        validationSchema={CarrierSchema}
        onSubmit={async (values, { setErrors, setStatus, setSubmitting }) => {
            setSuccess(false);
            setSubmitting(true);
            let formData = new FormData();
            Object.keys(values).forEach((k) => {
                formData.append(k, values[k]);
            })
            postRequest('/account/carrier/link', formData).then(d => {
                setSuccess(true);
                onSuccess(d);
                setTimeout(()=>{
                    if(mounted){
                        setSuccess(false);
                    }
                }, 3000)
            }).finally(() => {
                setSubmitting(false);
            })
        }}>
        {({ errors, handleBlur, handleChange, handleSubmit, isSubmitting, touched, values }) => (
            <form noValidate onSubmit={handleSubmit}>
                <MainCard title="Link Carrier">
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell width={200}>Carrier Name</TableCell>
                                <TableCell width={200}>Carrier Delay (Hours)</TableCell>
                                <TableCell>&nbsp;</TableCell>
                            </TableRow>
                        </TableHead>
                    <TableBody>
                    <TableRow>
                    {carriers.length > 0 && (<>
                        <TableCell sx={{alignItems: "center"}}>
                            <Select
                                id={'carrier-select'}
                                value={values.carrier}
                                name="carrier"
                                label="Carrier"
                                sx={{ width: 200 }}
                                onChange={handleChange}
                            >{carriers.map((c, i) => {
                                return <MenuItem key={i} value={c.id}>{c.name}</MenuItem>
                            })}
                            </Select>
                        </TableCell>
                        <TableCell>
                            <TextField id={'carrier-delay'} name="transitDelayHours" type="number" step="1" min={0} max={730} value={values.transitDelayHours} onChange={handleChange} />
                            {touched.transitDelayHours && errors.transitDelayHours && (
                                <FormHelperText error>
                                    {errors.transitDelayHours}
                                </FormHelperText>
                            )}
                        </TableCell>
                        <TableCell>
                            <Button
                                id={'carrier-submit'}
                                disableElevation
                                disabled={isSubmitting}
                                size="large"
                                type="submit"
                                variant="contained"
                                color={success ? "success" : "primary"}
                            >{success ? "Saved" : "Submit"}
                            </Button>
                        </TableCell>
                    </>)}
                    </TableRow>
                    </TableBody>
                    </Table>
                </MainCard>
            </form>
        )}
    </Formik>))
}

const CarriersLoading = () => {
    return (
        [...Array(5)].map((v, i)=>{
            return (
                <TableRow key={i}>
                    <TableCell>
                        <Skeleton variant="text" width="40%" />
                    </TableCell>
                    <TableCell>
                        <Skeleton variant="text" width="40%" />
                    </TableCell>
                </TableRow>
            )
        })
    )
}


const Carriers = () => {
    const [accountCarriers, setAccountCarriers] = React.useState([]);
    const [addCarrier, setAddCarrier] = React.useState(false);
    const [loading, setLoading] = React.useState(true);
    const loadCarriers = () => {
        setLoading(true);
        setAddCarrier(false);
        getRequest('/account/carriers').then((d) => {
            setAccountCarriers(d);
        }).finally(()=>{
            setLoading(false);
        })
    }
    React.useEffect(() => {
        loadCarriers();
    }, []);
    return (<>{addCarrier ? (
        <AddNewCarrier existingCarrierIds={accountCarriers.map((ac,i)=>ac.carrier_id)} onSuccess={() => loadCarriers()} />
    ) : (
        <MainCard title="Carriers Settings" secondary={<Button variant="outlined" onClick={() => setAddCarrier(true)} id="btn-add-carrier">Add Carrier</Button>}>
            {loading || accountCarriers.length > 0 ? (
            <>
            <Table>
                <TableHead>
                    <TableRow>
                        <TableCell width={300}>Carrier Name</TableCell>
                        <TableCell>Carrier Delay (hours)</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {loading ? <CarriersLoading /> : (<>
                    {accountCarriers.map((ac, i) => {
                        return <CarrierRow ac={ac} key={i} onSuccess={()=>loadCarriers()} />
                    })}
                    </>)}
                </TableBody>
            </Table>

            </>) : (
                <Notice>No transit carriers found. You will need to add one or more carriers to each warehouse in order to provide our most accurate data. If you have not integrated with your website, view our <a href={process.env.REACT_APP_DOCS_URL} target="_blank">documentation</a> to get started.</Notice>
            )}
        </MainCard>)}</>);
}

const CarrierRow = ({ac, onSuccess = () => {}}) => {
    const [success, setSuccess] = React.useState(false);
    const [mounted, setMounted] = React.useState(false);
    React.useEffect(() => {
        setMounted(true);
        return () => {
            setMounted(false);
        }
    }, []);
    return (<TableRow>
        <TableCell width={300}>{ac.carrier.name}</TableCell>
        <TableCell>
            <Formik
                initialValues={{
                    transitDelayHours: ac.carrier_transit_delay_hours,
                    submit: null
                }}
                validationSchema={CarrierTransitSchema}
                onSubmit={(values, { setErrors, setStatus, setSubmitting }) => {
                    setSubmitting(true);
                    let formData = new FormData();
                    Object.keys(values).forEach((k) => {
                        formData.append(k, values[k]);
                    })
                    postRequest('/account/carrier/update/' + ac.id, formData).then(d => {
                        if (d.errors) {
                            setErrors({ transitDelayHours: d.message });
                        }else{
                            setSuccess(true);
                            onSuccess();
                        }
                        setTimeout(()=>{
                            if(mounted){
                                setSuccess(false);
                            }
                        }, 3000)
                    }).catch(err => {
                        console.log(err)
                    }).finally(() => {
                        setSubmitting(false);
                    })
                }}>
                {({ errors, handleBlur, handleChange, handleSubmit, isSubmitting, touched, values, initialValues }) => (
                    <form onSubmit={handleSubmit}>
                        <Stack direction="row" spacing={3}>
                            <TextField name="transitDelayHours" type="number" value={values.transitDelayHours} onChange={handleChange} width={150} />

                            {initialValues.transitDelayHours != values.transitDelayHours && (
                            <LoadingButton
                                disableElevation
                                disabled={isSubmitting}
                                size="small"
                                type="submit"
                                variant="contained"
                                color={success ? "success" : "primary"}
                                loading={isSubmitting}
                            >{success ? "Updated" : "Update"}
                            </LoadingButton>)}
                            {touched.transitDelayHours && errors.transitDelayHours && (
                                <FormHelperText error>
                                    {errors.transitDelayHours}
                                </FormHelperText>
                            )}
                        </Stack>
                    </form>
                )}
            </Formik>
        </TableCell>
    </TableRow>
    )
}
export default Carriers;
